import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { IDVData } from '@shared/models/data-model';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { I18nService } from '@shared/services/i18n.service';
import { IdvService } from '@shared/services/idv.service';
import { NGXLogger } from 'ngx-logger';
import { Handle, Onfido, SdkParameters } from 'onfido-sdk-ui';
import { CompleteData } from 'onfido-sdk-ui/types/shared/SdkParameters';
import { OnfidoUIConfig } from './onfido.config';

const STATUS_CODE_WORKFLOW_NOT_AVAILABLE = 410;
@Component({
    selector: 'embedded-idv-with-onfido',
    templateUrl: './embedded-idv-with-onfido.component.html',
    styleUrl: './embedded-idv-with-onfido.component.scss'
})
export class EmbeddedIdvWithOnfidoComponent implements OnInit, OnDestroy {
    private onfidoSdkHandle: Handle;
    sdkInitalized = false;
    displayCheckEmailError = false;

    private readonly userAnalyticsEventListener: (event) => void;

    constructor(protected logger: NGXLogger,
        private readonly idvService: IdvService,
        private readonly amplitudeService: AmplitudeService,
        private readonly router: Router,
        private readonly i18nService: I18nService
    ) {
        this.userAnalyticsEventListener = (event) => {
            this.logger.debug("This is an ONFIDO event", event);
            this.amplitudeService.trackOnfidoWebSdkTraversal(event.detail?.eventName, event.detail?.isCrossDevice);
        };
    }


    ngOnInit(): void {
        this.i18nService.turnOffLanguageSelection();

        window.addEventListener('userAnalyticsEvent', this.userAnalyticsEventListener);

        const idvData = this.idvService.getCurrentIdvSessionData();

        this.idvService.generateOnfidoSdkToken(idvData.idvReference)
            .then((sdkToken: string) => {
                this.onfidoSdkHandle = this.initOnfido(sdkToken, idvData);
                this.sdkInitalized = true;
            })
            .catch((e) => {
                if (e.status === STATUS_CODE_WORKFLOW_NOT_AVAILABLE) {
                    this.displayCheckEmailError = true;
                }
            });
    }

    ngOnDestroy(): void {
        this.onfidoSdkHandle.tearDown();
        window.removeEventListener('userAnalyticsEvent', this.userAnalyticsEventListener);
    }

    private initOnfido(sdkToken: string, idvData: IDVData): Handle {
        const params: SdkParameters = {
            token: sdkToken,
            workflowRunId: idvData.idvReference,
            containerId: "onfidoContainer",
            theme: {
                name: "light",
                config: OnfidoUIConfig
            },
            onComplete: (data: CompleteData) => {
                this.logger.debug("IDV process completed", data);
                this.amplitudeService.trackOnfidoWebSdkIdvComplete();
                if (idvData.personExternalId) {
                    this.amplitudeService.trackInternalUserIdvCompleted();
                    this.router.navigate(['static/internal-user-identity-verified']);
                } else {
                    this.router.navigate(['static/identity-verified']);
                }
            },
            onError: (error) => {
                this.logger.debug("An error occurred", error);
                this.amplitudeService.trackOnfidoWebSdkError(error.type);
            }
        };
        return Onfido.init(params);
    }
}
